import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Loader2, Users, ZoomIn, ZoomOut, RotateCcw, Maximize2 } from 'lucide-react';
import { Tree, TreeNode } from 'react-organizational-chart';
import { apiGet } from '../../lib/api';

const DEPT_PALETTE = ['#1F7A6E', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4'];

function deptColor(name) {
  if (!name) return '#94a3b8';
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return DEPT_PALETTE[h % DEPT_PALETTE.length];
}

function getInitials(name) {
  return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

function NodeCard(props) {
  const { node, matched, isRoot } = props;
  const dimmed = matched && !node.__synthetic && !matched.has(node.id);
  const highlighted = matched && !node.__synthetic && matched.has(node.id);
  const stripe = isRoot || node.__synthetic ? '#1F7A6E' : deptColor(node.department || '');
  const teamSize = node.report_count != null ? node.report_count : ((node.reports && node.reports.length) || 0);
  const hasTeam = teamSize > 0;
  const initials = getInitials(node.name);

  let borderClass = 'border-slate-200 hover:border-[#1F7A6E] hover:shadow-md';
  if (highlighted) borderClass = 'border-amber-400 ring-2 ring-amber-300/40';
  else if (node.__synthetic) borderClass = 'border-[#1F7A6E]';

  return (
    <div
      className={'inline-flex flex-col items-stretch bg-white border rounded-sm shadow-sm transition-all duration-150 ' + borderClass + (dimmed ? ' opacity-30' : '')}
      style={{ width: 220 }}
      data-testid={'org-card-' + node.id}
    >
      <div className="h-1 rounded-t-sm" style={{ background: stripe }} />
      <div className="px-3 pt-3 pb-3 text-left">
        <div className="flex items-start gap-2.5">
          <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-display font-bold text-white" style={{ background: stripe }}>
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-[13px] text-slate-900 leading-tight truncate" title={node.name}>{node.name}</div>
            <div className="text-[10.5px] text-slate-500 font-medium leading-tight truncate mt-0.5" title={node.designation}>
              {node.designation || (node.__synthetic ? 'Organisation' : '\u2014')}
            </div>
          </div>
        </div>
        {!node.__synthetic ? (
          <div className="mt-2.5 flex items-center justify-between text-[10px] text-slate-500">
            <span className="font-mono">{node.emp_id || ''}</span>
            {hasTeam ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 rounded text-slate-700 font-display font-bold">
                <Users size={9} /> {teamSize}
              </span>
            ) : null}
          </div>
        ) : null}
        {!node.__synthetic && node.department ? (
          <div className="mt-1.5 text-[9.5px] font-display font-bold uppercase tracking-wider text-slate-500 truncate">{node.department}</div>
        ) : null}
      </div>
    </div>
  );
}

function renderTreeNodes(children, matched) {
  if (!children || !children.length) return null;
  return children.map((child) => (
    <TreeNode key={child.id} label={<NodeCard node={child} matched={matched} />}>
      {renderTreeNodes(child.reports, matched)}
    </TreeNode>
  ));
}

export default function OrgChart() {
  const [tree, setTree] = useState({ roots: [], total_employees: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [zoom, setZoom] = useState(0.85);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    apiGet('/hr/org-tree')
      .then((res) => setTree(res || { roots: [], total_employees: 0 }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const flat = useMemo(() => {
    const out = [];
    const walk = (n) => { out.push(n); (n.reports || []).forEach(walk); };
    (tree.roots || []).forEach(walk);
    return out;
  }, [tree]);

  const matched = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.trim().toLowerCase();
    return new Set(flat.filter((e) => {
      return (e.name || '').toLowerCase().includes(q)
        || (e.emp_id || '').toLowerCase().includes(q)
        || (e.designation || '').toLowerCase().includes(q)
        || (e.department || '').toLowerCase().includes(q);
    }).map((e) => e.id));
  }, [query, flat]);

  const treeRoot = useMemo(() => {
    const roots = tree.roots || [];
    if (!roots.length) return null;
    if (roots.length === 1) return roots[0];
    return {
      id: '__org_root__',
      name: 'ELR Industries',
      designation: 'Organisation',
      emp_id: '',
      department: '',
      reports: roots,
      report_count: tree.total_employees,
      __synthetic: true,
    };
  }, [tree]);

  const onZoomIn = () => setZoom((z) => Math.min(z + 0.1, 1.6));
  const onZoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.4));
  const onReset = () => setZoom(0.85);
  const onFit = () => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    el.scrollTo({ left: (el.scrollWidth - el.clientWidth) / 2, behavior: 'smooth' });
  };

  return (
    <div className="space-y-4" data-testid="org-chart">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">HR · Organisation</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Org Chart</h1>
          <p className="text-sm text-slate-600 mt-1">
            {tree.total_employees} employees · {(tree.roots || []).length} top-level node{(tree.roots || []).length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name / emp_id / dept…"
              className="pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm w-72 focus:border-[#1F7A6E] focus:outline-none"
              data-testid="org-search-input"
            />
          </div>
          <div className="flex items-center bg-white border border-slate-200 rounded-sm">
            <button onClick={onZoomOut} className="px-2.5 py-2 hover:bg-slate-50 text-slate-600" title="Zoom out" data-testid="org-zoom-out"><ZoomOut size={14} /></button>
            <span className="px-2 text-[11px] font-mono text-slate-500 tabular-nums w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={onZoomIn} className="px-2.5 py-2 hover:bg-slate-50 text-slate-600 border-l border-slate-200" title="Zoom in" data-testid="org-zoom-in"><ZoomIn size={14} /></button>
            <button onClick={onReset} className="px-2.5 py-2 hover:bg-slate-50 text-slate-600 border-l border-slate-200" title="Reset" data-testid="org-zoom-reset"><RotateCcw size={14} /></button>
            <button onClick={onFit} className="px-2.5 py-2 hover:bg-slate-50 text-slate-600 border-l border-slate-200" title="Center" data-testid="org-fit"><Maximize2 size={14} /></button>
          </div>
        </div>
      </div>

      <div
        ref={wrapperRef}
        className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-md overflow-auto"
        style={{ minHeight: '70vh', maxHeight: '80vh' }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-72 gap-2 text-sm text-slate-500">
            <Loader2 size={14} className="animate-spin" /> Loading organisation…
          </div>
        ) : !treeRoot ? (
          <div className="flex flex-col items-center justify-center h-72 text-slate-500">
            <Users size={36} className="mb-3 text-slate-300" />
            <div className="text-sm">No employees yet. Add employees in HR → Employee Master.</div>
          </div>
        ) : (
          <div
            className="p-8 inline-block min-w-full"
            style={{ transform: 'scale(' + zoom + ')', transformOrigin: 'top center', transition: 'transform 200ms ease-out' }}
            data-testid="org-tree-canvas"
          >
            <Tree
              lineWidth="1.5px"
              lineColor="#cbd5e1"
              lineBorderRadius="10px"
              label={<NodeCard node={treeRoot} matched={matched} isRoot />}
            >
              {renderTreeNodes(treeRoot.reports, matched)}
            </Tree>
          </div>
        )}
      </div>
    </div>
  );
}
